import { getDay } from 'date-fns';
import { get } from 'lodash';
import React, { useCallback, useContext, useMemo } from 'react';
import { FormContext, FormContextType } from '../../../form/FormContext';
import FormWidget from '../../../form/FormWidget';
import { FORM_WIDGET_COMPONENTS } from '../../../form/formConstants';
import { useI18n } from '../../../i18n/I18n';
import { parseApiDate } from '../../../utils/dateTime';
import { SCHEDULE_FORM_WIDGET_FIELD_NAMES } from '../constants';
import { ScheduleFormWidgetValue, SupportedSchedulerDurationUnit } from '../types';
import useScheduleFormWidgetContext from '../useScheduleFormWidgetContext';
import { checkIsTimeBasedDurationUnit } from '../utils/common-schedule-utils';
import { resolveStateFieldName } from '../utils/form-utils';

export interface ScheduleDateTimeFormWidgetProps {
    variant: 'start' | 'end';
    name?: string;
    disabled?: boolean;
}

// Map day names from Calendar API to JavaScript Date.getDay() values
const DAY_NAME_TO_JS_DAY_NUMBER: Record<string, number> = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6
};

export default function ScheduleDateTimeFormWidget({
    variant,
    name: nameProp,
    disabled: disabledProp = false
}: ScheduleDateTimeFormWidgetProps) {
    const t = useI18n();

    const { loading, error, disabled, getFormStateByFieldName } = useScheduleFormWidgetContext();
    const { state } = useContext<FormContextType<ScheduleFormWidgetValue>>(FormContext);

    const dateWidgetName = resolveStateFieldName([
        nameProp,
        variant === 'start' ? SCHEDULE_FORM_WIDGET_FIELD_NAMES.START_DATE : SCHEDULE_FORM_WIDGET_FIELD_NAMES.END_DATE
    ]);
    const timeWidgetName = resolveStateFieldName([
        nameProp,
        variant === 'start' ? SCHEDULE_FORM_WIDGET_FIELD_NAMES.START_TIME : SCHEDULE_FORM_WIDGET_FIELD_NAMES.END_TIME
    ]);

    const durationUnitFieldName = resolveStateFieldName([nameProp, SCHEDULE_FORM_WIDGET_FIELD_NAMES.DURATION_UNIT]);
    const durationUnit = getFormStateByFieldName(durationUnitFieldName) as SupportedSchedulerDurationUnit;
    const shouldShowTimeFields = checkIsTimeBasedDurationUnit(durationUnit);

    const durationSpecifiedFieldName = resolveStateFieldName([
        nameProp,
        SCHEDULE_FORM_WIDGET_FIELD_NAMES.DURATION_SPECIFIED
    ]);
    const isDurationSpecified = getFormStateByFieldName(durationSpecifiedFieldName) as boolean;

    const isWidgetDisabled = disabled || disabledProp || loading || (variant === 'end' && isDurationSpecified);

    const dateWidgetErrors = get(error, dateWidgetName);
    const timeWidgetErrors = get(error, timeWidgetName);
    const startDateFieldName = resolveStateFieldName([nameProp, SCHEDULE_FORM_WIDGET_FIELD_NAMES.START_DATE]);
    const startDateValue = getFormStateByFieldName(startDateFieldName) as string | undefined;

    const workingDayNumbers = useMemo(() => {
        const calendar = nameProp ? get(state, `${nameProp}.calendar`) : state.calendar;

        if (!calendar || !calendar.workingTime?.timeIntervals || calendar.workingTime.timeIntervals.length === 0) {
            return new Set<number>();
        }

        return new Set(
            calendar.workingTime.timeIntervals
                .map((timeInterval) => DAY_NAME_TO_JS_DAY_NUMBER[timeInterval.day?.toLocaleLowerCase()])
                .filter((dayNumber): dayNumber is number => dayNumber !== undefined)
        );
    }, [nameProp, state]);

    const dateReference = useMemo(() => {
        if (variant !== 'end' || !startDateValue) {
            return undefined;
        }

        return parseApiDate(startDateValue);
    }, [startDateValue, variant]);

    const shouldDisableDate = useCallback(
        (date: Date): boolean => {
            if (workingDayNumbers.size > 0) {
                const dayOfWeek = getDay(date);
                return !workingDayNumbers.has(dayOfWeek);
            }

            return false;
        },
        [workingDayNumbers]
    );

    return (
        <>
            <FormWidget
                component={FORM_WIDGET_COMPONENTS.date}
                label={t(`scheduleFormWidget.${variant}Date.label`)}
                name={dateWidgetName}
                disabled={isWidgetDisabled}
                referenceDate={dateReference}
                shouldDisableDate={shouldDisableDate}
                deferKeyboardDateCommitUntilBlur
                hasErrors={Boolean(dateWidgetErrors)}
                errors={dateWidgetErrors}
            />
            {shouldShowTimeFields && (
                <FormWidget
                    component={FORM_WIDGET_COMPONENTS.time}
                    label={t(`scheduleFormWidget.${variant}Time.label`)}
                    name={timeWidgetName}
                    disabled={isWidgetDisabled}
                    hasErrors={Boolean(timeWidgetErrors)}
                    errors={timeWidgetErrors}
                />
            )}
        </>
    );
}
